import { DataProcessingOption } from "models/DataProcesses";
import BaseService from "services/BaseService";
import {
    Organization,
    SAMLDataRequest,
    SAMLProviderData,
} from "./types/Organization.type";
import { Tier } from "./types/Tier.type";
import { serializeKboUserAgreement } from "./kboUserAgreementConfiguration";
import {
    FilterTypes,
    ModelComputeFrequency,
    ModelComputeOptionEnabled,
    PIIExtractionOption,
    RedactionOption,
    TranscriptionFilterOptionEnabled,
} from "../../../models/ModelCompute";
import { InteractionType } from "models/InteractionType";

interface ModelComputeOptionsEditRequest {
    modelOption: string;
    enabled: boolean;
    modelQuota?: number;
    quotaFrequency?: ModelComputeFrequency;
    organizationStructureMemberIds: string[] | null;
}

interface TranscriptionFilterOptionEditRequest {
    filterType: FilterTypes;
    enabled: boolean;
    mediaType: InteractionType;
    filterValue: number;
}

class OrganizationService extends BaseService {
    protected urlBase: string = "api/Organizations";

    public async getAllOrganizations(
        page = 0,
        rowsPerPage = 100,
    ): Promise<Organization[]> {
        return this.get(
            new URLSearchParams({
                page: page.toString(10),
                rowsPerPage: rowsPerPage.toString(10),
            }),
            "GetPagedList",
        );
    }

    public async getOrganization(
        organizationId: string,
    ): Promise<Organization> {
        const res: Organization = await this.post(
            JSON.stringify({ organizationId }),
            new URLSearchParams({}),
            "GetOrganizationById",
        );

        res.EnableSamlSso =
            !!res.saml2EntityId ||
            res.samlProviders?.some((provider) => provider.isEnabled);
        return res;
    }

    public async createOrganization(organization: Organization) {
        const params = new URLSearchParams({});
        return this.post(
            JSON.stringify(organization),
            params,
            "CreateOrganization",
            { returnJson: true },
        );
    }

    public async getCheckDbConnection(
        databaseConnectionString: string,
        databaseVersion: number,
    ): Promise<Organization> {
        const res: Organization = await this.get(
            new URLSearchParams({
                ConnectionString: databaseConnectionString,
                DatabaseVersion: databaseVersion.toString(),
            }),
            "CheckDbConnection",
        );
        return res;
    }

    public async updateOrganization(organization: Organization) {
        const params = new URLSearchParams({});

        const formattedOrg = {
            ...organization,
            organizationConfigurations: {
                ...organization.organizationConfigurations,
                evalAcknowledgeDays:
                    organization.evalAcknowledgeDays?.toString(),
                interactionsWithinDays:
                    organization.interactionsWithinDays?.toString(),
                calendarOrBusinessDays: organization.calendarOrBusinessDays,
                dailyInteractionAlertUserIds:
                    organization.dailyInteractionAlertUserIds
                        ? organization.dailyInteractionAlertUserIds.join(",")
                        : "",
                hierarchyIdsToAlertOnZeroInteractions:
                    organization.hierarchyIdsToAlertOnZeroInteractions
                        ? organization.hierarchyIdsToAlertOnZeroInteractions.join(
                              ",",
                          )
                        : "",
                MaxDistinctGroupableClassifierOutputs:
                    organization.MaxDistinctGroupableClassifierOutputs?.toString() ||
                    organization.organizationConfigurations
                        ?.MaxDistinctGroupableClassifierOutputs,
                unredactedTranscriptionVisibility:
                    organization.unredactedTranscriptionVisibility?.toString() ??
                    organization.organizationConfigurations
                        ?.unredactedTranscriptionVisibility ??
                    "false",
                requireMfa:
                    organization.requireMfa?.toString() ??
                    organization.organizationConfigurations?.requireMfa ??
                    "false",
                mfaAuthenticatorAppEnabled:
                    organization.mfaAuthenticatorAppEnabled?.toString() ??
                    organization.organizationConfigurations
                        ?.mfaAuthenticatorAppEnabled ??
                    "false",
                mfaEmailEnabled:
                    organization.mfaEmailEnabled?.toString() ??
                    organization.organizationConfigurations?.mfaEmailEnabled ??
                    "false",
            },
        };

        delete formattedOrg.licensedData;

        // Left out when blank: the save removes configuration it isn't sent.
        const kboUserAgreement = serializeKboUserAgreement(organization);
        if (kboUserAgreement) {
            formattedOrg.organizationConfigurations.KboUserAgreement =
                kboUserAgreement;
        } else {
            delete formattedOrg.organizationConfigurations.KboUserAgreement;
        }

        return this.put(
            params,
            "UpdateOrganization",
            JSON.stringify(formattedOrg),
            { returnJson: true },
        );
    }

    public async saveOrganization(organization: Organization) {
        return organization.id
            ? this.updateOrganization(organization)
            : this.createOrganization(organization);
    }

    public async deleteOrganization(id: string) {
        if (!id) {
            throw new Error(`Can't delete Organization without valid ID`);
        }
        return this.delete(new URLSearchParams({ id }), "DeleteOrganization");
    }

    public async getHierarchy(organizationId: string): Promise<Tier[]> {
        const res = await this.post(
            JSON.stringify({ organizationId }),
            new URLSearchParams({}),
            "GetServiceHierarchyForOrganization",
        );

        return res?.tiers;
    }

    public async saveHierarchy(
        id: string,
        hierarchy: Tier[],
        tiersToRemove: string[] = [],
    ) {
        const params = new URLSearchParams({});
        const res = await this.post(
            JSON.stringify({
                organizationId: id,
                tiers: hierarchy,
                tiersToRemove,
            }),
            params,
            "SaveServiceHierarchyForOrganization",
        );

        return res;
    }

    public async getDataProcessingOptions(): Promise<DataProcessingOption[]> {
        return this.get(
            new URLSearchParams({ page: "0" }),
            "GetDataProcessing",
        );
    }

    public async getModelComputeOptions(
        id: string,
    ): Promise<ModelComputeOptionEnabled[]> {
        return this.post(
            JSON.stringify({ organizationId: id }),
            new URLSearchParams({}),
            "GetModelComputeOptions",
        );
    }

    public async getTranscriptionFilterOptions(
        id: string,
    ): Promise<TranscriptionFilterOptionEnabled[]> {
        return this.post(
            JSON.stringify({ organizationId: id }),
            new URLSearchParams({}),
            "GetTranscriptionFilterOptions",
        );
    }

    public async updateModelComputeOptions(
        id: string,
        modelComputeOptions: ModelComputeOptionsEditRequest[],
    ) {
        const body = JSON.stringify({
            orgId: id,
            editModelComputeOptions: modelComputeOptions,
        });
        return this.put(
            new URLSearchParams({}),
            "UpdateModelComputeOptions",
            body,
        );
    }

    public async getVoiceRedactionFactor(id: string): Promise<number> {
        return this.get(
            new URLSearchParams({ organizationId: id }),
            "GetVoiceRedactionFactor",
        );
    }

    public async updateVoiceRedactionFactor(
        id: string,
        voiceRedactionFactor?: number,
    ) {
        if (voiceRedactionFactor === undefined) return;
        return this.put(
            new URLSearchParams({}),
            "UpdateVoiceRedactionFactor",
            JSON.stringify({
                organizationId: id,
                voiceRedactionFactor: voiceRedactionFactor,
            }),
        );
    }

    public async updateTranscriptionFilterOptions(
        id: string,
        editTranscriptionFilterOptions: TranscriptionFilterOptionEditRequest[],
    ) {
        const body = JSON.stringify({
            orgId: id,
            editTranscriptionFilterOptions,
        });
        return this.put(
            new URLSearchParams({}),
            "UpdateTranscriptionFilterOptions",
            body,
        );
    }

    public async saveSAMLOptions(
        data: SAMLDataRequest,
    ): Promise<SAMLProviderData[]> {
        const params = new URLSearchParams({});
        return this.post(
            JSON.stringify(data),
            params,
            "UpdateSamlSettingsForOrganization",
            { returnJson: true },
        );
    }

    public async getLogsForOrganization(
        id: string,
        offset: number,
        items: number,
    ): Promise<any[]> {
        const body = JSON.stringify({
            organizationId: id,
            offset: offset.toString(),
            limit: items.toString(),
            source: "SAML",
        });
        return this.post(
            body,
            new URLSearchParams({}),
            "GetLogsForOrganization",
        );
    }
    public async getSamplingTargetForOrganizationStructureMember(
        id: string,
        historyLimit: number = 10,
        historyOffset: number = 0,
    ) {
        const res = await this.get(
            new URLSearchParams({
                OrganizationStructureMemberId: id,
                HistoryLimit: JSON.stringify(historyLimit),
                HistoryOffset: JSON.stringify(historyOffset),
            }),
            "GetSamplingTargetForOrganizationStructureMember",
        );
        return res;
    }

    public async updateSamplingTarget(
        organizationStructureMemberId: string,
        samplingTargetTimePeriod: string,
        samplingTargetUnitOfMeasure: string,
        value: number,
        monitoredByUserId: string,
        hierarchyCodes?: string[],
    ) {
        const res = await this.post(
            JSON.stringify({
                OrganizationStructureMemberId: organizationStructureMemberId,
                TimePeriod: samplingTargetTimePeriod,
                UnitOfMeasure: samplingTargetUnitOfMeasure,
                Value: value,
                MonitoredByUserId: monitoredByUserId,
                HierarchyCodes: hierarchyCodes,
            }),
            new URLSearchParams({}),
            "UpdateSamplingTarget",
        );
        return res;
    }

    public async getTopLevelServiceHierarchiesForOrganization(
        orgId: string,
    ): Promise<{ organizationId: string; tiers: Tier[] }> {
        const res = await this.get(
            new URLSearchParams({
                orgId,
            }),
            "GetTopLevelServiceHierarchiesForOrganization",
        );
        return res;
    }

    public async getServiceHierarchyLeaf(osmId: string): Promise<Tier[]> {
        const res = await this.get(
            new URLSearchParams({
                osmId,
            }),
            "GetServiceHierarchyLeaf",
        );
        return res;
    }

    public async getRedactionProcessingOptions(): Promise<string[]> {
        const res = await this.get(
            new URLSearchParams({}),
            "GetRedactionProcessingOptions",
        );
        return res;
    }

    public async getActiveRedactionProcessingOptions(): Promise<
        RedactionOption[]
    > {
        const res = await this.get(
            new URLSearchParams({}),
            "GetActiveRedactionProcessingOptions",
        );
        return res;
    }

    public async updateRedactionProcessingOptions(
        id: string,
        activeRedactionOptions: string[],
        allRedactionOptions: string[],
    ) {
        const res = await this.put(
            new URLSearchParams({}),
            "UpdateRedactionProcessingOptions",
            JSON.stringify({
                orgId: id,
                editRedactionProcessingOptions: allRedactionOptions.map(
                    (optString) => ({
                        redactionOption: optString,
                        enabled: activeRedactionOptions.includes(optString),
                    }),
                ),
            }),
        );
        return res;
    }
    public async getPIIExtractionOptions(): Promise<string[]> {
        const res = await this.get(
            new URLSearchParams({}),
            "GetPIIExtractionOptions",
        );
        return res;
    }

    public async getActivePIIExtractionOptions(): Promise<
        PIIExtractionOption[]
    > {
        const res = await this.get(
            new URLSearchParams({}),
            "GetActivePIIExtractionOptions",
        );
        return res;
    }

    public async updatePIIExtractionOptions(
        id: string,
        activePIIOptions: string[],
        allPIIOptions: string[],
    ) {
        const res = await this.put(
            new URLSearchParams({}),
            "UpdatePIIExtractionOptions",
            JSON.stringify({
                orgId: id,
                editRedactionProcessingOptions: allPIIOptions.map(
                    (optString) => ({
                        redactionOption: optString,
                        enabled: activePIIOptions.includes(optString),
                    }),
                ),
            }),
        );
        return res;
    }
}
// eslint-disable-next-line import/no-anonymous-default-export
export default new OrganizationService();
